import React from 'react'
import { useTranslation } from 'react-i18next'
import ClockIcons from '@/icons/ClockIcons'
import {
    ERROR_SERVICE_INCIDENT_TICKET_KEY,
    MEETING_TICKET_KEY,
    TICKET_TYPE_GROUP_MEETING,
    TICKET_TYPE_GROUP_SUPPORT_REQUEST,
} from '../../../constants'
import TicketPriority from '../../Forms/TicketPriority'
import TicketMarjorable from '../../Forms/TicketMarjorable'
import TicketRequestedAt from '../../Forms/TicketRequestedAt'
import TicketTimeRepply from '../../Forms/TicketTimeReply'
import TicketEstimatedCompletion from '../../Forms/TicketEstimatedCompletion'
import TicketDowntime from '../../Forms/TicketDowntime'
import TicketSecurityTag from '../../Forms/TicketSecurityTag'
import SectionHeader from '../section-header'
import { CreateTicketSectionProps, SLAInfo } from '../types'

interface PriorityScheduleSectionProps extends CreateTicketSectionProps {
    slaInfo: SLAInfo
    /** Pass ticketId in edit mode to auto-detect existing downtime logs */
    ticketId?: number
}

const PriorityScheduleSection: React.FC<PriorityScheduleSectionProps> = ({
    control,
    watch,
    setValue,
    slaInfo,
    ticketId,
}) => {
    const { t } = useTranslation()
    const ticketTypeGroup = watch('ticket_type_group')
    const ticketTypeKey = watch('ticket_type_key')
    const ticketPriority = watch('ticket_priority')
    const serviceId = watch('service_id')

    return (
        <>
            {/* Downtime section for error_service tickets */}
            {ticketTypeGroup === 'error_service' &&
                !!serviceId &&
                Number(serviceId) > 0 && (
                    <div
                        className="bg-white rounded-md p-4 border border-gray-100"
                        data-tour="ticket-downtime"
                    >
                        <TicketDowntime
                            control={control}
                            watch={watch}
                            setValue={setValue}
                            ticketRequestedAt={watch('ticket_requested_at')}
                            ticketId={ticketId}
                        />
                    </div>
                )}

            {/* Priority & Schedule */}
            <div className="bg-white rounded-md p-4 border border-gray-100">
                <SectionHeader
                    title={t('ticket-priority-schedule')}
                    icon={<ClockIcons className="w-4 h-4" />}
                />
                <div className="space-y-2">
                    <div data-tour="ticket-priority">
                        <TicketPriority
                            control={control}
                            onChange={(value) =>
                                setValue('ticket_priority', value)
                            }
                        />
                    </div>
                    {ticketTypeKey === ERROR_SERVICE_INCIDENT_TICKET_KEY &&
                        ticketPriority === 'high' && (
                            <div data-tour="ticket-marjorable">
                                <TicketMarjorable control={control} />
                            </div>
                        )}
                    {ticketTypeGroup === 'error_service' && (
                        <div data-tour="ticket-security-tag" className="!mt-4">
                            <TicketSecurityTag control={control} />
                        </div>
                    )}
                    <div data-tour="ticket-requested-at">
                        <TicketRequestedAt
                            control={control}
                            ticketTypeGroup={ticketTypeGroup}
                            ticketTypeKey={ticketTypeKey}
                            onChange={(value) => {
                                if (value) {
                                    setValue('ticket_time_repply', value)
                                }
                            }}
                        />
                    </div>
                    <div data-tour="ticket-time-reply">
                        <TicketTimeRepply
                            control={control}
                            disabled={
                                ticketTypeGroup === TICKET_TYPE_GROUP_MEETING
                            }
                        />
                    </div>
                    {ticketTypeKey !== MEETING_TICKET_KEY && (
                        <div data-tour="ticket-estimated-completion">
                            <TicketEstimatedCompletion
                                control={control}
                                disabled={
                                    // Support request can edit ONLY if no SLA
                                    ticketTypeGroup !==
                                        TICKET_TYPE_GROUP_SUPPORT_REQUEST ||
                                    !!slaInfo.time
                                }
                                slaTime={slaInfo.time}
                                slaTimeFormat={slaInfo.timeFormat}
                                slaSource={slaInfo.source}
                            />
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}

export default PriorityScheduleSection
